import { Howl } from 'howler';
export class SoundManager {
  private swipeSound: Howl;
  private collisionSound: Howl;
  private bgMusic: Howl;
  private initialized = false;
  constructor() {
    try {
      this.swipeSound = this.createToneSound(440, 0.05, 'sine');
      this.collisionSound = this.createToneSound(120, 0.3, 'sawtooth');
      this.bgMusic = this.createBgMusic();
      this.initialized = true;
    } catch {
      // Create empty sounds if generation fails
      this.swipeSound = new Howl({ src: [''], volume: 0 });
      this.collisionSound = new Howl({ src: [''], volume: 0 });
      this.bgMusic = new Howl({ src: [''], volume: 0 });
    }
  }
  private createToneSound(frequency: number, duration: number, type: OscillatorType): Howl {
    try {
      const sampleRate = 22050;
      const samples = Math.floor(sampleRate * duration);
      const buffer = new Float32Array(samples);
      for (let i = 0; i < samples; i++) {
        const t = i / sampleRate;
        const envelope = Math.pow(1 - t / duration, 2);
        let sample: number;
        if (type === 'sine') {
          sample = Math.sin(2 * Math.PI * frequency * t) * envelope * 0.4;
        } else {
          sample = ((2 * ((frequency * t) % 1)) - 1) * envelope * 0.3;
        }
        buffer[i] = sample;
      }
      const wavData = this.encodeWav(buffer, sampleRate);
      const base64 = this.arrayBufferToBase64(wavData);
      const dataUri = `data:audio/wav;base64,${base64}`;
      return new Howl({ src: [dataUri], volume: 0.4 });
    } catch {
      return new Howl({ src: [''], volume: 0 });
    }
  }
  private createBgMusic(): Howl {
    try {
      const sampleRate = 22050;
      const bpm = 140;
      const beatDuration = 60 / bpm;
      const bars = 4;
      const totalDuration = bars * 4 * beatDuration;
      const samples = Math.floor(sampleRate * totalDuration);
      const buffer = new Float32Array(samples);
      // Musical pattern
      const bassNotes = [55, 55, 82.5, 55, 73.4, 55, 82.5, 61.7];
      const melodyNotes = [220, 261.6, 329.6, 392, 329.6, 261.6, 220, 196];
      for (let i = 0; i < samples; i++) {
        const t = i / sampleRate;
        const beatIndex = Math.floor(t / beatDuration);
        const beatPhase = (t % beatDuration) / beatDuration;
        // Bass: punchy sawtooth
        const bassFreq = bassNotes[beatIndex % bassNotes.length];
        const bassEnv = Math.max(0, 1 - beatPhase * 3);
        const bass = ((2 * ((bassFreq * t) % 1)) - 1) * bassEnv * 0.2;
        // Hi-hat: noise bursts on 8th notes
        const eighthPhase = (t % (beatDuration / 2)) / (beatDuration / 2);
        const hihatEnv = Math.max(0, 1 - eighthPhase * 10);
        const hihat = (Math.random() * 2 - 1) * hihatEnv * 0.05;
        // Melody: sine with smooth envelope
        const halfBeat = beatDuration * 2;
        const melodyIndex = Math.floor(t / halfBeat);
        const melodyFreq = melodyNotes[melodyIndex % melodyNotes.length];
        const melodyPhase = (t % halfBeat) / halfBeat;
        const melodyEnv =
          melodyPhase < 0.15
            ? Math.min(1, melodyPhase * 6) * (1 - Math.max(0, (melodyPhase - 0.7) * 3))
            : 0;
        const melody = Math.sin(2 * Math.PI * melodyFreq * t) * melodyEnv * 0.12;
        buffer[i] = Math.max(-1, Math.min(1, bass + hihat + melody));
      }
      const wavData = this.encodeWav(buffer, sampleRate);
      const base64 = this.arrayBufferToBase64(wavData);
      const dataUri = `data:audio/wav;base64,${base64}`;
      return new Howl({ src: [dataUri], volume: 0.3, loop: true });
    } catch {
      return new Howl({ src: [''], volume: 0 });
    }
  }
  private encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
    const numChannels = 1;
    const bitsPerSample = 16;
    const blockAlign = (numChannels * bitsPerSample) / 8;
    const byteRate = sampleRate * blockAlign;
    const dataSize = samples.length * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    const writeString = (offset: number, s: string) => {
      for (let i = 0; i < s.length; i++) {
        view.setUint8(offset + i, s.charCodeAt(i));
      }
    };
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  }
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
  playSwipe() {
    if (!this.initialized) return;
    try {
      this.swipeSound.play();
    } catch {
      /* ignore */
    }
  }
  playCollision() {
    if (!this.initialized) return;
    try {
      this.collisionSound.play();
    } catch {
      /* ignore */
    }
  }
  startMusic() {
    if (!this.initialized) return;
    try {
      if (!this.bgMusic.playing()) {
        this.bgMusic.play();
      }
    } catch {
      /* ignore */
    }
  }
  stopMusic() {
    if (!this.initialized) return;
    try {
      this.bgMusic.stop();
    } catch {
      /* ignore */
    }
  }
  pauseMusic() {
    if (!this.initialized) return;
    try {
      this.bgMusic.pause();
    } catch {
      /* ignore */
    }
  }
  resumeMusic() {
    if (!this.initialized) return;
    try {
      if (!this.bgMusic.playing()) {
        this.bgMusic.play();
      }
    } catch {
      /* ignore */
    }
  }
  destroy() {
    if (!this.initialized) return;
    try {
      this.swipeSound.unload();
      this.collisionSound.unload();
      this.bgMusic.unload();
    } catch {
      /* ignore */
    }
  }
}